import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import useEmblaCarousel from "embla-carousel-react";
import {
  ArrowLeft, Gem, MapPin, Phone, Globe, MessageCircle, Instagram,
  Clock, Cigarette, Share2, Edit2, Trash2, Car, Navigation, QrCode,
  Store, AlertCircle, Loader2, ExternalLink, Footprints,
} from "lucide-react";
import { toast } from "sonner";
import { api, getMe } from "../lib/api";
import * as guestStore from "../lib/guestStorage";
import { BottomNav } from "../components/BottomNav";
import ShareQRWhatsAppDialog from "../components/ShareQRWhatsAppDialog";
import { sendRideRequest, sendBusinessContact } from "../lib/sendRideRequest";
import DriverQRModal from "../components/DriverQRModal";
import { haversineKm, fmtDistanceWalk } from "../lib/geo";
import { useGps } from "../lib/useGps";
import { useI18n } from "../lib/i18n";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";

const APP_URL = process.env.REACT_APP_BACKEND_URL || "";

/**
 * In-app Custom Location detail page. Layout mirrors PlaceProfile so creators
 * see their saved gems in a consistent, business-style profile UI.
 *
 * Path: /my-places/custom/:id
 */
export default function CustomLocationDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useI18n();
  const [doc, setDoc] = useState(null);
  const [user, setUser] = useState(null);
  const [err, setErr] = useState(null);
  const { coords: gpsCoords, state: gpsState, requestGps } = useGps();
  const [shareOpen, setShareOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [requesting, setRequesting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [emblaRef] = useEmblaCarousel({ loop: false, dragFree: true });
  const [qrOpen, setQrOpen] = useState(false);

  useEffect(() => {
    getMe().then(setUser).catch(() => setUser(null));
    // iter106 — Guest-saved locations have ids that start with "g_" and live
    // only in localStorage. Read them from guestStore so the detail page
    // works offline without ever hitting the API.
    if (id && id.startsWith("g_")) {
      const local = guestStore.getCustomLocations().find((c) => c.id === id);
      if (local) { setDoc(local); }
      else { setErr("Could not load location"); }
      return;
    }
    api.get(`/custom-locations/${id}`)
      .then(({ data }) => setDoc(data))
      .catch((e) => setErr(e.response?.data?.detail || "Could not load location"));
  }, [id]);

  // iter106 — Guests own any custom location whose id starts with "g_"
  // (those live in their own localStorage). For authed users, ownership
  // matches by owner_id.
  const isGuestOwned = Boolean(id && id.startsWith("g_"));
  const isCreator = isGuestOwned || (user && doc && user.user_id === doc.owner_id);
  const isAdmin = user && user.role === "admin";

  if (err) {
    return (
      <main className="min-h-screen flex flex-col items-center justify-center px-8 text-center" style={{ background: "#0B0B0B" }}>
        <AlertCircle className="w-9 h-9 text-gold mb-4" strokeWidth={1.5} />
        <h1 className="font-display text-2xl text-foreground mb-2">Unavailable</h1>
        <p className="text-foreground/60 text-sm">{err}</p>
        <Link to="/journey" className="mt-6 px-6 py-3 text-gold border border-gold-strong">Back to My Journey</Link>
      </main>
    );
  }
  if (!doc) return <main className="min-h-screen" style={{ background: "#0B0B0B" }} />;

  const photos = (doc.photos || []).filter((p) => p?.url);
  const publicUrl = `${APP_URL}/places/custom/${id}?t=${doc.share_token || ""}`;
  const status = doc.directory_request_status || "none";

  const onRequestDirectory = async () => {
    setRequesting(true);
    try {
      await api.post(`/custom-locations/${id}/directory-request`);
      const { data } = await api.get(`/custom-locations/${id}`);
      setDoc(data);
      toast.success("Request sent — our team will review it.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not request");
    } finally {
      setRequesting(false);
    }
  };

  const onDelete = async () => {
    setDeleting(true);
    try {
      // iter106 — Guest locations live only in localStorage.
      if (id && id.startsWith("g_")) {
        const next = guestStore.getCustomLocations().filter((c) => c.id !== id);
        try { localStorage.setItem("habano.custom_locations", JSON.stringify(next)); } catch { /* ignore */ }
      } else {
        await api.delete(`/custom-locations/${id}`);
      }
      toast.success("Location deleted");
      navigate("/my-places/custom");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not delete");
    } finally {
      setDeleting(false);
      setDeleteOpen(false);
    }
  };

  // If converted → redirect to public business listing (creator only)
  const isConverted = Boolean(doc.converted_business_id);

  return (
    <main data-testid="custom-location-detail" className="min-h-screen pb-28" style={{ background: "#0B0B0B" }}>
      {/* Photo carousel */}
      <div className="relative w-full overflow-hidden" style={{ aspectRatio: "16/10", background: "#171717" }}>
        {photos.length === 0 ? (
          <div className="absolute inset-0 grid place-items-center">
            <Gem className="w-16 h-16 text-gold opacity-60" strokeWidth={1.5} />
          </div>
        ) : (
          <div className="embla h-full" ref={emblaRef}>
            <div className="embla__container flex h-full">
              {photos.map((p, i) => (
                <div key={i} className="embla__slide flex-[0_0_100%] h-full">
                  <img loading="lazy" src={p.url} alt={`${doc.name} ${i + 1}`} className="w-full h-full object-cover" />
                </div>
              ))}
            </div>
          </div>
        )}
        <button
          onClick={() => navigate(-1)}
          aria-label="Back"
          className="absolute top-4 left-4 w-10 h-10 grid place-items-center rounded-full"
          style={{ background: "rgba(11,11,11,0.6)", color: "#fff", backdropFilter: "blur(6px)" }}
        >
          <ArrowLeft className="w-5 h-5" strokeWidth={1.5} />
        </button>
      </div>

      <div className="px-5 mt-5">
        <span className="inline-flex items-center gap-1 px-2.5 py-1 border text-[11px] uppercase tracking-widest text-gold" style={{ borderColor: "rgba(232,184,75,0.4)" }}>
          <Gem className="w-3 h-3" strokeWidth={1.5} /> Local Gem
        </span>
        <h1 data-testid="cl-name" className="font-display text-3xl text-foreground mt-3 leading-tight">{doc.name}</h1>
        {doc.address && (
          <div className="flex items-center gap-1.5 text-[14px] text-foreground/65 mt-2">
            <MapPin className="w-3.5 h-3.5 text-gold" strokeWidth={1.5} /> {doc.address}
          </div>
        )}
        {(() => {
          if (doc.lat == null || doc.lng == null) return null;
          if (gpsCoords) {
            const dist = fmtDistanceWalk(haversineKm(gpsCoords, { lat: doc.lat, lng: doc.lng }));
            if (!dist) return null;
            return (
              <div
                className="mt-2 inline-flex items-center gap-1.5"
                style={{ color: "#B8B2A7", fontFamily: "'DM Sans', sans-serif", fontSize: 13 }}
                data-testid="cl-distance"
              >
                <Footprints className="w-3.5 h-3.5" strokeWidth={1.5} />
                <span>{dist}</span>
              </div>
            );
          }
          if (gpsState === "denied" || gpsState === "idle") {
            return (
              <button
                type="button"
                onClick={requestGps}
                data-testid="cl-enable-location"
                className="mt-2 inline-flex items-center gap-1 hover:opacity-80 transition-opacity"
                style={{ color: "#E8B84B", fontFamily: "'DM Sans', sans-serif", fontSize: 11 }}
              >
                <MapPin className="w-3 h-3" strokeWidth={1.5} /> Enable location
              </button>
            );
          }
          return null;
        })()}
        {doc.description && (
          <p className="text-[15px] text-foreground/80 mt-5 leading-relaxed">{doc.description}</p>
        )}
        {(doc.hours || doc.smoking_policy) && (
          <div className="mt-5 space-y-2">
            {doc.hours && (
              <div className="flex items-center gap-2 text-[13px] text-foreground/70">
                <Clock className="w-4 h-4 text-gold" strokeWidth={1.5} /> {doc.hours}
              </div>
            )}
            {doc.smoking_policy && (
              <div className="flex items-center gap-2 text-[13px] text-foreground/70">
                <Cigarette className="w-4 h-4 text-gold" strokeWidth={1.5} /> {smokingLabel(doc.smoking_policy)}
              </div>
            )}
          </div>
        )}

        {/* Action buttons — same shape as business profile */}
        <div className="mt-7 space-y-3">
          {(doc.lat != null && doc.lng != null) && (
            <a
              data-testid="cl-navigate"
              href={`https://www.google.com/maps/dir/?api=1&destination=${doc.lat},${doc.lng}`}
              target="_blank" rel="noreferrer"
              className="w-full inline-flex items-center justify-center gap-2 text-gold py-3"
              style={{ border: "1px solid rgba(232,184,75,0.4)", borderRadius: 10, minHeight: 48 }}
            >
              <Navigation className="w-4 h-4" strokeWidth={1.5} /> Navigate
            </a>
          )}

          <div className="grid grid-cols-2 gap-3">
            {doc.whatsapp && (
              <button type="button" data-testid="cl-whatsapp" onClick={() => sendBusinessContact({ name: doc.name, whatsapp: doc.whatsapp }, { businessId: doc.id, source: "custom-location-detail" })}
                className="inline-flex items-center justify-center gap-2 text-gold py-3"
                style={{ border: "1px solid rgba(232,184,75,0.4)", borderRadius: 10, minHeight: 48, background: "transparent", width: "100%" }}>
                <MessageCircle className="w-4 h-4" strokeWidth={1.5} /> WhatsApp
              </button>
            )}
            {doc.instagram && (
              <a data-testid="cl-instagram" href={`https://instagram.com/${doc.instagram}`} target="_blank" rel="noreferrer"
                className="inline-flex items-center justify-center gap-2 text-gold py-3"
                style={{ border: "1px solid rgba(232,184,75,0.4)", borderRadius: 10, minHeight: 48 }}>
                <Instagram className="w-4 h-4" strokeWidth={1.5} /> Instagram
              </a>
            )}
            {doc.phone && (
              <a data-testid="cl-phone" href={`tel:${doc.phone.replace(/\s/g, "")}`}
                className="inline-flex items-center justify-center gap-2 text-gold py-3"
                style={{ border: "1px solid rgba(232,184,75,0.4)", borderRadius: 10, minHeight: 48 }}>
                <Phone className="w-4 h-4" strokeWidth={1.5} /> Call
              </a>
            )}
            {doc.website && (
              <a data-testid="cl-website" href={doc.website.startsWith("http") ? doc.website : `https://${doc.website}`}
                target="_blank" rel="noreferrer"
                className="inline-flex items-center justify-center gap-2 text-gold py-3"
                style={{ border: "1px solid rgba(232,184,75,0.4)", borderRadius: 10, minHeight: 48 }}>
                <Globe className="w-4 h-4" strokeWidth={1.5} /> Website
              </a>
            )}
          </div>

          {/* iter104 — Two-row ride layout: prominent Ride + Directions/Share side-by-side */}
          {((doc.lat != null && doc.lng != null) || doc.address) && (
            <div className="mt-3 space-y-2" data-testid="cl-action-buttons">
              <button
                type="button"
                data-testid="cl-request-driver"
                onClick={() => sendRideRequest(
                  { name: doc.name, address: doc.address, lat: doc.lat, lng: doc.lng },
                  { source: "custom-location-detail" },
                )}
                className="w-full inline-flex items-center justify-center gap-2 hover:opacity-95 transition-opacity"
                style={{
                  height: 52,
                  background: "linear-gradient(180deg, #F5CC55, #E8B84B)",
                  color: "#0B0B0B", border: "none", borderRadius: 12,
                  fontFamily: "'DM Sans', sans-serif", fontSize: 16, fontWeight: 700,
                  boxShadow: "0 4px 16px rgba(232,184,75,0.35)",
                }}
              >
                <Car style={{ width: 18, height: 18 }} strokeWidth={2} /> {t("ride_request_driver")}
              </button>
              {(doc.lat != null && doc.lng != null) && (
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    data-testid="cl-get-qr"
                    onClick={() => setQrOpen(true)}
                    className="inline-flex items-center justify-center gap-1.5 transition-colors hover:bg-white/[0.04]"
                    style={{
                      height: 44, borderRadius: 10, padding: "0 10px",
                      background: "transparent", color: "#E8B84B",
                      fontFamily: "'DM Sans', sans-serif", fontSize: 13, fontWeight: 600,
                      border: "1.5px solid rgba(232,184,75,0.55)",
                    }}
                  >
                    <QrCode className="w-4 h-4" strokeWidth={2} /> {t("ride_view_directions")}
                  </button>
                  <button
                    type="button"
                    data-testid="cl-share-location"
                    onClick={async () => {
                      const url = `https://www.google.com/maps?q=${doc.lat},${doc.lng}`;
                      const text = `${doc.name}${doc.address ? `\n${doc.address}` : ""}\n${url}`;
                      if (navigator.share) {
                        try { await navigator.share({ title: doc.name, text, url }); }
                        catch { /* ignore */ }
                      } else {
                        try { await navigator.clipboard.writeText(text); toast.success(t("ride_share_location")); }
                        catch { /* ignore */ }
                      }
                    }}
                    className="inline-flex items-center justify-center gap-1.5 transition-colors hover:bg-white/[0.04]"
                    style={{
                      height: 44, borderRadius: 10, padding: "0 10px",
                      background: "transparent", color: "#E8B84B",
                      fontFamily: "'DM Sans', sans-serif", fontSize: 13, fontWeight: 600,
                      border: "1.5px solid rgba(232,184,75,0.55)",
                    }}
                  >
                    <Share2 className="w-4 h-4" strokeWidth={2} /> {t("ride_share_location")}
                  </button>
                </div>
              )}
              {(doc.lat != null && doc.lng != null) && (
                <div className="text-center" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 11, color: "#9A9490" }}>
                  {t("ride_qr_works_offline")}
                </div>
              )}
            </div>
          )}
        </div>

        {/* iter67 — Upgrade card: "Is this a business?" */}
        {isCreator && !isConverted && (
          <section className="mt-6" data-testid="cl-upgrade-card">
            <Link
              to={`/my-places/custom/${id}/edit`}
              className="block hover:opacity-95 transition-opacity"
              data-testid="cl-upgrade-link"
              style={{
                background: "rgba(232,184,75,0.06)",
                border: "1px solid rgba(232,184,75,0.3)",
                borderRadius: 12,
                padding: 12,
                display: "flex",
                alignItems: "center",
                gap: 12,
              }}
            >
              <span className="grid place-items-center rounded-full shrink-0" style={{ width: 36, height: 36, background: "rgba(232,184,75,0.10)", border: "1px solid rgba(232,184,75,0.35)" }}>
                <Store className="w-4 h-4 text-gold" strokeWidth={1.5} />
              </span>
              <div className="flex-1 min-w-0">
                <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14, fontWeight: 500, color: "#F5F1E8", lineHeight: 1.2 }}>
                  {t("sal_upgrade_q")}
                </div>
                <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 12, color: "#B0AAA3", lineHeight: 1.3, marginTop: 2 }}>
                  {t("sal_upgrade_sub")}
                </div>
              </div>
              <span
                className="inline-flex items-center gap-1 px-3 py-1.5 shrink-0"
                style={{
                  background: "linear-gradient(135deg, #d4b25a 0%, #E8B84B 100%)",
                  color: "#0B0B0B",
                  borderRadius: 999,
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: 11,
                  fontWeight: 600,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  minHeight: 32,
                }}
              >
                <Edit2 className="w-3 h-3" strokeWidth={1.5} />
                Edit
              </span>
            </Link>
          </section>
        )}

        {/* Creator-only: notes, share, edit, delete, directory request */}
        {(isCreator || isAdmin) && doc.notes && (
          <section data-testid="cl-my-notes" className="mt-8 pt-6 border-t" style={{ borderColor: "rgba(232,184,75,0.18)" }}>
            <h2 className="eyebrow text-gold">My Notes — only you can see this</h2>
            <p className="text-[14px] italic text-foreground/65 mt-2 whitespace-pre-wrap">{doc.notes}</p>
          </section>
        )}

        {isCreator && !isConverted && (
          <section className="mt-8 pt-6 border-t" style={{ borderColor: "rgba(232,184,75,0.18)" }}>
            <h2 className="eyebrow text-gold">Owner tools</h2>

            <div className="grid grid-cols-2 gap-3 mt-3">
              <button
                type="button"
                data-testid="cl-share-btn"
                onClick={() => setShareOpen(true)}
                className="inline-flex items-center justify-center gap-2 text-gold py-3"
                style={{ border: "1px solid rgba(232,184,75,0.4)", borderRadius: 10, minHeight: 48 }}
              >
                <Share2 className="w-4 h-4" strokeWidth={1.5} /> Share
              </button>
              <Link
                to={`/my-places/custom/${id}/edit`}
                data-testid="cl-edit-btn"
                className="inline-flex items-center justify-center gap-2 text-gold py-3"
                style={{ border: "1px solid rgba(232,184,75,0.4)", borderRadius: 10, minHeight: 48 }}
              >
                <Edit2 className="w-4 h-4" strokeWidth={1.5} /> Edit
              </Link>
            </div>

            {/* Directory request CTA */}
            <div className="mt-3">
              {status === "none" || status === "rejected" ? (
                <button
                  type="button"
                  data-testid="cl-request-directory-btn"
                  onClick={onRequestDirectory}
                  disabled={requesting}
                  className="w-full inline-flex items-center justify-center gap-2 text-gold py-3"
                  style={{ border: "1px solid rgba(232,184,75,0.4)", borderRadius: 10, minHeight: 48 }}
                >
                  {requesting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Store className="w-4 h-4" strokeWidth={1.5} />}
                  Request to Add to Habano Directory
                </button>
              ) : status === "pending" ? (
                <div
                  data-testid="cl-request-pending"
                  className="w-full text-center py-3 text-amber-300 text-sm"
                  style={{ border: "1px solid rgba(245,158,11,0.4)", background: "rgba(245,158,11,0.06)", borderRadius: 10 }}
                >
                  Request Pending
                </div>
              ) : null}
            </div>

            {/* Delete */}
            <button
              type="button"
              data-testid="cl-delete-btn"
              onClick={() => setDeleteOpen(true)}
              className="mt-5 w-full inline-flex items-center justify-center gap-2 text-destructive text-sm py-3"
              style={{ border: "1px solid rgba(239,68,68,0.35)", borderRadius: 10, minHeight: 48 }}
            >
              <Trash2 className="w-4 h-4" strokeWidth={1.5} /> Delete location
            </button>
          </section>
        )}

        {/* Approved → directory banner */}
        {isCreator && isConverted && (
          <section
            data-testid="cl-converted-banner"
            className="mt-8 px-4 py-4 flex items-center gap-3 text-[14px] text-gold"
            style={{ background: "rgba(232,184,75,0.10)", border: "1px solid rgba(232,184,75,0.4)", borderRadius: 12 }}
          >
            <Store className="w-5 h-5" strokeWidth={1.5} />
            <span className="flex-1">
              Now live in the Habano directory — manage from your Business Dashboard
            </span>
            <Link to="/business" className="inline-flex items-center text-gold">
              <ExternalLink className="w-4 h-4" />
            </Link>
          </section>
        )}
      </div>

      <BottomNav />

      <DriverQRModal
        business={doc}
        open={qrOpen}
        onClose={() => setQrOpen(false)}
        testidPrefix="cl-driver-qr"
      />

      <ShareQRWhatsAppDialog
        open={shareOpen}
        onOpenChange={setShareOpen}
        locationName={doc.name}
        publicUrl={publicUrl}
      />

      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent
          data-testid="cl-delete-confirm"
          className="max-w-sm p-0 border-0"
          style={{ background: "#171717", border: "1px solid rgba(239,68,68,0.4)", borderRadius: 14 }}
        >
          <DialogHeader className="px-6 pt-6">
            <DialogTitle className="font-display text-xl text-foreground">Delete this location?</DialogTitle>
            <DialogDescription className="text-foreground/60 text-[13px] mt-1">
              This action can&apos;t be undone. Anyone who saved this location will lose access immediately.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="px-6 pb-6 gap-2">
            <button onClick={() => setDeleteOpen(false)} className="flex-1 py-2.5 text-foreground/70 text-sm" style={{ border: "1px solid rgba(255,255,255,0.12)", borderRadius: 8 }}>
              Cancel
            </button>
            <button
              data-testid="cl-delete-confirm-btn"
              onClick={onDelete}
              disabled={deleting}
              className="flex-1 py-2.5 text-sm font-display"
              style={{ background: "#EF4444", color: "#fff", borderRadius: 8 }}
            >
              {deleting ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : "Delete"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </main>
  );
}

function smokingLabel(p) {
  return ({
    allowed: "Smoking allowed",
    not_allowed: "Smoking not allowed",
    outdoor_only: "Smoking — outdoor only",
  })[p] || p;
}
